import React, { ReactElement } from 'react'
import { Box, Card, Link, Typography, useTheme } from '@mui/material'

import {
  AccreditationHealthRow,
  AccreditationKey,
  AccreditationTotals,
  ACCREDITATION_COLUMNS,
} from 'src/v3/content-pipeline/accreditation-health.slice'

let EMPTY_TOTALS: AccreditationTotals = {
  missing: 0,
  notPursued: 0,
  noExpirationDate: 0,
  current: 0,
  approaching: 0,
  expired: 0,
}

function KPITile({
  label,
  count,
  color,
}: {
  label: string
  count: number
  color: string
}): ReactElement {
  return (
    <Card
      variant="outlined"
      sx={{
        p: 1.5,
        flex: 1,
        minWidth: 130,
        borderLeft: '3px solid',
        borderLeftColor: color,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {count}
      </Typography>
    </Card>
  )
}

function CoverageBar({
  totals,
  total,
}: {
  totals: AccreditationTotals
  total: number
}): ReactElement {
  let theme = useTheme()
  let segments: { count: number; color: string }[] = [
    { count: totals.current, color: theme.palette.success.main },
    { count: totals.approaching, color: theme.palette.warning.main },
    { count: totals.expired, color: theme.palette.error.main },
    { count: totals.missing, color: theme.palette.error.dark },
    { count: totals.notPursued, color: theme.palette.text.disabled },
    { count: totals.noExpirationDate, color: theme.palette.info.main },
  ]

  return (
    <Box
      sx={{
        display: 'flex',
        height: 6,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'background.paperDark',
      }}
    >
      {segments.map(
        (segment, i) =>
          segment.count > 0 && (
            <Box
              key={i}
              sx={{
                width: `${(segment.count / total) * 100}%`,
                bgcolor: segment.color,
              }}
            />
          ),
      )}
    </Box>
  )
}

type Props = {
  rows: AccreditationHealthRow[]
  overall: AccreditationTotals
  totalsByAccreditation: Partial<Record<AccreditationKey, AccreditationTotals>>
  approachingWindowMonths: number
  onViewAccreditation: (key: AccreditationKey) => void
}

export default function AccreditationOverviewTab({
  rows,
  overall,
  totalsByAccreditation,
  approachingWindowMonths,
  onViewAccreditation,
}: Props): ReactElement {
  let totalDraftsInProgress = rows.reduce(
    (sum, row) =>
      sum +
      ACCREDITATION_COLUMNS.filter((col) => row.accreditations[col.key].pending)
        .length,
    0,
  )

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
        <KPITile label="Missing" count={overall.missing} color="error.main" />
        <KPITile
          label="Not Pursued"
          count={overall.notPursued}
          color="text.disabled"
        />
        <KPITile label="Expired" count={overall.expired} color="error.main" />
        <KPITile
          label={`Approaching (${approachingWindowMonths}mo)`}
          count={overall.approaching}
          color="warning.main"
        />
        <KPITile label="Current" count={overall.current} color="success.main" />
        <KPITile
          label="No Expiration"
          count={overall.noExpirationDate}
          color="info.main"
        />
        <KPITile
          label="Drafts In Progress"
          count={totalDraftsInProgress}
          color="text.disabled"
        />
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Coverage by accreditation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Click a body to see its open gaps in the Needs Attention tab.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 1.5,
        }}
      >
        {ACCREDITATION_COLUMNS.map((col) => {
          let totals = totalsByAccreditation[col.key] ?? EMPTY_TOTALS
          let total = rows.length
          let pendingCount = rows.filter(
            (row) => row.accreditations[col.key].pending,
          ).length

          return (
            <Card
              key={col.key}
              variant="outlined"
              component="button"
              onClick={() => onViewAccreditation(col.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onViewAccreditation(col.key)
                }
              }}
              aria-label={`View ${col.label} accreditation gaps`}
              tabIndex={0}
              sx={{
                p: 1.5,
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                cursor: 'pointer',
                '&:focus': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '2px',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  mb: 1,
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>{col.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {totals.current}/{total} current
                </Typography>
              </Box>
              <CoverageBar totals={totals} total={total || 1} />
              <Typography
                variant="caption"
                color={pendingCount > 0 ? 'primary' : 'text.secondary'}
                sx={{ display: 'block', mt: 1 }}
              >
                {pendingCount > 0 ? (
                  <Link component="span" underline="hover">
                    {pendingCount} update{pendingCount === 1 ? '' : 's'} in
                    review
                  </Link>
                ) : (
                  'No updates in review'
                )}
              </Typography>
            </Card>
          )
        })}
      </Box>
    </Box>
  )
}
